import { useState, useEffect, useCallback } from "react";
import { siteData } from "@/store/siteData";
import { partners } from "@/mocks/partners";
import { apiGetMessages, ApiMessage } from "@/utils/apiClient";

const msgStatusColors: Record<string, string> = {
  new: "bg-[#3B82F6]/20 text-[#B8C5D8] border border-[#3B82F6]/30",
  read: "bg-white/10 text-[#7B8FAF] border border-white/10",
  replied: "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30",
};

const msgStatusLabel: Record<string, string> = {
  new: "Yeni",
  read: "Okundu",
  replied: "Yanıtlandı",
};

export default function DashboardOverview() {
  const projects = siteData.getProjects().filter(p => !p.archived);
  const completedProjects = projects.filter((p) => p.status === "Tamamlandı").length;
  const ongoingProjects = projects.filter((p) => p.status === "Devam Ediyor").length;
  const plannedProjects = projects.filter((p) => p.status === "Planlama").length;
  const totalUnits = projects.reduce((acc, p) => acc + (p.units || 0), 0);
  const totalArea = projects.reduce((acc, p) => {
    const n = parseInt(String(p.area || "0").replace(/\./g, "").replace(/[^0-9]/g, ""));
    return acc + (isNaN(n) ? 0 : n);
  }, 0);

  const [msgs, setMsgs] = useState<ApiMessage[]>([]);
  const [msgLoading, setMsgLoading] = useState(true);

  const loadMessages = useCallback(async () => {
    setMsgLoading(true);
    try {
      const data = await apiGetMessages();
      setMsgs(data);
    } catch {
      // sessizce — mock yedek yok
    } finally {
      setMsgLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMessages();
    const interval = setInterval(loadMessages, 30_000);
    return () => clearInterval(interval);
  }, [loadMessages]);

  const newMessages = msgs.filter((m) => m.status === "new").length;

  const stats = [
    { label: "Toplam Proje", value: projects.length.toString(), icon: "ri-building-2-line", change: `${completedProjects} tamamlandı` },
    { label: "Stratejik Ortak", value: partners.length.toString(), icon: "ri-links-line", change: "Aktif ortaklıklar" },
    { label: "Toplam Konut", value: `${totalUnits.toLocaleString("tr")}+`, icon: "ri-home-4-line", change: "Tüm projeler" },
    { label: "Yeni Mesaj", value: msgLoading ? "…" : newMessages.toString(), icon: "ri-mail-open-line", change: "Yanıt bekliyor", highlight: newMessages > 0 },
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white tracking-tight">Genel Bakış</h1>
        <p className="text-[#7B8FAF] text-sm mt-1">Yenigül Yapı İnşaat — Site Yönetim Özeti</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat) => (
          <div
            key={stat.label}
            className={`rounded-xl p-5 border ${
              stat.highlight
                ? "bg-[#3B82F6]/10 border-[#3B82F6]/40"
                : "bg-[#141E35] border-[#2B3654]"
            }`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#3B82F6]/20">
                <i className={`${stat.icon} text-[#B8C5D8] text-lg`}></i>
              </div>
            </div>
            <p className="text-2xl font-black text-white">{stat.value}</p>
            <p className="text-[#7B8FAF] text-xs mt-1">{stat.label}</p>
            <p className={`text-xs mt-1 ${stat.highlight ? "text-[#B8C5D8]" : "text-[#5E7398]"}`}>{stat.change}</p>
          </div>
        ))}
      </div>

      {/* Project Status + Partner Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Project Status */}
        <div className="bg-[#141E35] border border-[#2B3654] rounded-xl p-6">
          <h2 className="text-white font-semibold mb-5 flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-pie-chart-2-line text-[#B8C5D8]"></i>
            </div>
            Proje Durumu Özeti
          </h2>
          <div className="space-y-3">
            {[
              { label: "Tamamlandı", count: completedProjects, color: "bg-emerald-500", pct: projects.length ? (completedProjects / projects.length) * 100 : 0 },
              { label: "Devam Ediyor", count: ongoingProjects, color: "bg-[#3B82F6]", pct: projects.length ? (ongoingProjects / projects.length) * 100 : 0 },
              { label: "Planlama", count: plannedProjects, color: "bg-amber-500", pct: projects.length ? (plannedProjects / projects.length) * 100 : 0 },
            ].map((item) => (
              <div key={item.label}>
                <div className="flex justify-between text-sm mb-1.5">
                  <span className="text-[#A5B5CC]">{item.label}</span>
                  <span className="text-white font-medium">{item.count} proje</span>
                </div>
                <div className="h-1.5 bg-[#1E2B4A] rounded-full overflow-hidden">
                  <div
                    className={`h-full ${item.color} rounded-full transition-all duration-700`}
                    style={{ width: `${item.pct}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-5 pt-4 border-t border-[#2B3654] grid grid-cols-2 gap-4">
            <div>
              <p className="text-[#7B8FAF] text-xs">Toplam Alan</p>
              <p className="text-white font-bold text-lg">{(totalArea / 1000).toFixed(0)}K m²</p>
            </div>
            <div>
              <p className="text-[#7B8FAF] text-xs">Ortalama Proje Büyüklüğü</p>
              <p className="text-white font-bold text-lg">{projects.length ? Math.round(totalArea / projects.length / 1000) : 0}K m²</p>
            </div>
          </div>
        </div>

        {/* Partner Overview */}
        <div className="bg-[#141E35] border border-[#2B3654] rounded-xl p-6">
          <h2 className="text-white font-semibold mb-5 flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-team-line text-[#B8C5D8]"></i>
            </div>
            Stratejik Ortaklar
          </h2>
          <div className="space-y-3">
            {partners.map((partner) => (
              <div key={partner.id} className="flex items-center justify-between py-2 border-b border-[#1E2B4A] last:border-0">
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">{partner.name}</p>
                  <p className="text-[#7B8FAF] text-xs">{partner.sector}</p>
                </div>
                <div className="flex items-center gap-3 ml-3 flex-shrink-0">
                  <span className="text-[#B8C5D8] text-xs">{partner.projects}+ proje</span>
                  <span className="text-[#5E7398] text-xs">{partner.since}&rsquo;den beri</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Messages */}
      <div className="bg-[#141E35] border border-[#2B3654] rounded-xl p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-white font-semibold flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-mail-line text-[#B8C5D8]"></i>
            </div>
            Son Mesajlar
            {newMessages > 0 && (
              <span className="ml-2 bg-[#3B82F6] text-white text-xs px-2 py-0.5 rounded-full">{newMessages} yeni</span>
            )}
          </h2>
          <button
            onClick={loadMessages}
            disabled={msgLoading}
            className="w-8 h-8 flex items-center justify-center text-[#7B8FAF] hover:text-white border border-[#2B3654] rounded-lg cursor-pointer hover:border-[#3B82F6] transition-all disabled:opacity-40"
            title="Yenile"
          >
            <i className={`ri-refresh-line text-sm ${msgLoading ? "animate-spin" : ""}`}></i>
          </button>
        </div>

        {msgLoading && msgs.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <i className="ri-loader-4-line animate-spin text-2xl text-[#3B82F6]"></i>
          </div>
        ) : msgs.length === 0 ? (
          <div className="py-12 text-center">
            <i className="ri-inbox-line text-4xl text-[#5E7398]"></i>
            <p className="text-[#7B8FAF] text-sm mt-3">Henüz mesaj yok</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#2B3654]">
                  <th className="text-left text-[#5E7398] font-medium pb-3 pr-4 text-xs uppercase tracking-widest">Gönderen</th>
                  <th className="text-left text-[#5E7398] font-medium pb-3 pr-4 text-xs uppercase tracking-widest hidden md:table-cell">Konu</th>
                  <th className="text-left text-[#5E7398] font-medium pb-3 pr-4 text-xs uppercase tracking-widest hidden lg:table-cell">Proje / Konu</th>
                  <th className="text-left text-[#5E7398] font-medium pb-3 text-xs uppercase tracking-widest">Durum</th>
                </tr>
              </thead>
              <tbody>
                {msgs.slice(0, 6).map((msg) => (
                  <tr key={msg.id} className="border-b border-[#1E2B4A] last:border-0">
                    <td className="py-3 pr-4">
                      <p className="text-white font-medium">{msg.name}</p>
                      <p className="text-[#7B8FAF] text-xs">{msg.date}</p>
                    </td>
                    <td className="py-3 pr-4 hidden md:table-cell">
                      <p className="text-[#A5B5CC] truncate max-w-[200px]">{msg.subject}</p>
                    </td>
                    <td className="py-3 pr-4 hidden lg:table-cell">
                      <p className="text-[#7B8FAF] text-xs">{msg.project || "—"}</p>
                    </td>
                    <td className="py-3">
                      <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${msgStatusColors[msg.status] || msgStatusColors.read}`}>
                        {msgStatusLabel[msg.status] || msg.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
